import { PlayArrowRounded } from '@mui/icons-material'
import { TableBody, TableCell, TableRow } from '@mui/material'
import { useAssignTasksMutation } from '@reader_task_list_view/hooks/useAssignTasksMutation'
import { EnhancedTableBodyProps } from '@reader_task_list_view/reader_task_list.types'
import { getComparator } from '@reader_task_list_view/utils'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import PopupMenuList from './PopupMenuList'
import TaskStateChip from './TaskStateChip'
import TaskTypeChip from './TaskTypeChip'
import Toast from './Toast'
import { launchDicomViewerForReader } from '@common/utils/ohifUtils'
import { formatTaskDate } from '@qc_task_list_view/utils'
import { useSigningContext } from '@features/reader_task_list_iam_view/pages/hooks/useSigningContext'

export default function EnhancedTableBodyMyTasks({
  rows,
  order,
  orderBy,
  authedUserToken,
  authedUserId,
  isRawDataEnabled,
  isBluerockEnabled,
}: EnhancedTableBodyProps) {
  const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows])
  const { mutateAsync: assignTask } = useAssignTasksMutation()
  const { studyId } = useParams()

  const { openSigningModal } = useSigningContext()

  const handleAssignTask = async (task_ids: string[]) => {
    if (authedUserId && studyId && authedUserToken && task_ids.length > 0) {
      const taskId = task_ids[0]
      await assignTask({
        query: {
          read_task_ids: [taskId],
          study_id: studyId,
        },
      })
        .then((res) => {
          toast(
            <Toast
              message="You were assigned task"
              task_ids={res.processed_task_ids.map((task) => {
                return task.split('-')[0]
              })}
              variant="success"
              data-testid="success-toast"
            />,
          )
          launchDicomViewerForReader(taskId, studyId, isRawDataEnabled ? 2 : isBluerockEnabled ? 3 : 0)
          openSigningModal(taskId, isRawDataEnabled)
        })
        .catch((error) => {
          toast(<Toast message={(error as AxiosError).message} variant="error" data-testid="error-toast" />)
        })
    } else {
      console.error('Missing required data for assigning task or launching viewer.')
      toast(<Toast message="Cannot assign task: Missing required information." variant="error" />)
    }
  }

  const handleRowClick = async (task_ids: string[], is_in_progress: boolean) => {
    if (authedUserId && studyId && authedUserToken && task_ids.length > 0) {
      const taskId = task_ids[0]
      if (is_in_progress) {
        openSigningModal(taskId, isRawDataEnabled)
      } else {
        handleAssignTask([taskId])
      }
    } else {
      console.error('Missing required data for opening summary page.')
      toast(<Toast message="Cannot open summary page: Missing required information." variant="error" />)
    }
  }

  return (
    <TableBody>
      {visibleRows.map((row) => {
        if (!row?.task_id) return null
        return (
          <TableRow
            tabIndex={-1}
            hover
            key={row.task_id}
            onClick={() => {
              handleRowClick([row.task_id!], row.status === 'IN_PROGRESS')
            }}
            sx={{
              cursor: 'pointer',
              borderBottom: '1px solid rgba(220, 227, 229, 1)',
            }}
          >
            <TableCell align="left" data-testid="task-pseudo-id-cell">
              {row.task_pseudo_id}
            </TableCell>
            <TableCell align="left" data-testid="blind-code-cell">
              {row.blind_code}
            </TableCell>
            <TableCell align="left" data-testid="task-type-cell">
              <TaskTypeChip taskType={row.task_type} />
            </TableCell>
            <TableCell align="left" data-testid="priority-cell">
              {row.priority}
            </TableCell>
            <TableCell align="left" data-testid="created-at-cell">
              {formatTaskDate(row.created_at)}
            </TableCell>
            <TableCell align="left" data-testid="due-date-cell">
              {formatTaskDate(row.due_date)}
            </TableCell>
            <TableCell align="left" data-testid="status-cell">
              <TaskStateChip taskState={row.status} />
            </TableCell>
            {row.status === 'IN_PROGRESS' ? (
              <TableCell align="right" data-testid="actions-cell">
                {studyId && (
                  <PopupMenuList read_task_id={row.task_id} read_pseudo_id={row.task_pseudo_id} study_id={studyId} />
                )}
              </TableCell>
            ) : (
              <TableCell
                align="right"
                data-testid="start-cell"
                onClick={(e) => {
                  e.stopPropagation()
                  handleAssignTask([row.task_id!])
                }}
              >
                <PlayArrowRounded
                  sx={{
                    cursor: 'pointer',
                    color: 'text.primary',
                  }}
                />
              </TableCell>
            )}
          </TableRow>
        )
      })}
    </TableBody>
  )
}
